import { createContext, useContext, useEffect, useState } from "react";

const STORE_KEY = "permitcheck_lang";

const DICT = {
  en: {
    // Header
    "header.subtitle": "Know Before You Buy, Build or Invest.",
    "brand.tagline": "Know Before You Buy, Build or Invest.",
    "header.live": "Live ArcGIS",
    "header.toggleTheme": "Toggle theme",
    "header.language": "Language",
    // Hero
    "hero.eyebrow": "Official Public Data · Miami-Dade County & City of Miami",
    "hero.title1": "Know the Property",
    "hero.title2": "Before You Make the Decision.",
    "hero.subtitle":
      "Search official property records, permits, violations, zoning and inspections from one place.",
    "search.valueprop": "Enter an address. Understand the property. Make a better decision.",
    "search.categories": "Permits · Violations · Zoning · Property Records · Inspections",
    // Search
    "search.placeholder": "Enter a property address",
    "search.button": "Check Property",
    "search.loading": "Checking…",
    "search.try": "Try",
    // Benefits
    "benefits.heading": "One search. A clearer decision.",
    "benefits.buy.title": "Buy",
    "benefits.buy.desc": "Understand important property information before purchasing.",
    "benefits.build.title": "Build",
    "benefits.build.desc": "Check zoning, jurisdiction and development rules before you build.",
    "benefits.invest.title": "Invest",
    "benefits.invest.desc": "Assess property use and development potential before you invest.",
    // Recent
    "recent.title": "Recent lookups",
    "recent.clear": "Clear",
    // Empty / error
    "empty.title": "Start with an address",
    "empty.subtitle": "Try one of the sample addresses above to see a full, source-cited property report.",
    "error.title": "Nothing to show",
    "error.notFound": "No matching property found.",
    "error.failed": "Lookup failed. A government service may be temporarily unavailable.",
    "toast.failed": "Lookup failed. Please try again.",
    // Report
    "report.siteAddress": "Site Address",
    "report.folio": "Parcel Folio",
    "report.copy": "Copy",
    "report.copied": "Copied",
    "report.municipality": "Municipality / Jurisdiction",
    "report.lotSize": "Lot Size",
    "report.propertyUse": "Property Use (DOR)",
    "report.landUse": "Land Use",
    "report.owner": "Owner of Record",
    // Zoning
    "zoning.header": "Miami 21 · Primary Zoning",
    "zoning.transect": "Transect",
    "zoning.intensity": "Intensity",
    "zoning.flr": "FLR",
    "zoning.about": "About Miami 21 →",
    // Building
    "building.header": "Building & Valuation",
    "building.yearBuilt": "Year Built",
    "building.units": "Units",
    "building.buildings": "Buildings",
    "building.heatedArea": "Heated Area",
    "building.landValue": "Land Value",
    "building.totalValue": "Total Value",
    // Source badge
    "source.official": "Official data source",
    "source.retrieved": "Retrieved {x}",
    "source.opensRest": "Opens ArcGIS REST JSON endpoint in new tab",
    // Service status
    "status.header": "Government Service Health",
    "status.live": "Live · No Mock Data",
    "status.probing": "Probing endpoints…",
    "status.phase2": " · Phase 2 (architecture ready)",
    "status.offline": "offline",
    // Map
    "map.header": "Parcel Geometry",
    "map.openMaps": "Open in Maps",
    "map.none": "No parcel geometry available.",
    "map.centroid": "Centroid {lat}, {lng} · WGS84 · Miami-Dade Parcels (Layer 26)",
    // Provenance
    "prov.open": "View raw provenance JSON",
    "prov.title": "Normalized Provenance Payload",
    // Footer
    "footer.disclaimer":
      "Wise Property Search queries live public ArcGIS REST servers operated by Miami-Dade County and the City of Miami. Data is provided as-is from official sources for informational purposes and is not a substitute for an official records search or survey.",
    // Dashboard
    "dash.title": "Property Check",
    "sum.permits": "Permits",
    "sum.openPermits": "Open Permits",
    "sum.violations": "Violations",
    "sum.openViolations": "Open Violations",
    "sum.failedInspections": "Failed Inspections",
    "sum.zoning": "Zoning",
    "sum.na": "N/A",
    "sec.overview": "Property Overview",
    "sec.permits": "Permits",
    "sec.violations": "Violations",
    "sec.zoning": "Zoning",
    "sec.inspections": "Inspections",
    "sec.records": "Property Records",
    "sec.sources": "Official Sources",
    "permit.number": "Permit #",
    "permit.type": "Type",
    "permit.desc": "Description",
    "permit.issued": "Issued",
    "permit.status": "Status",
    "permit.authority": "Authority",
    "permit.open": "Open",
    "permit.closed": "Closed",
    "notice.none": "No records found in the currently connected official sources.",
    "notice.unavailable": "Source temporarily unavailable.",
    "notice.notConnected": "No records available from the currently connected official sources.",
    "rec.owner": "Owner",
    "rec.use": "Property Use",
    "rec.landUse": "Land Use",
    "rec.lot": "Lot Size",
    "rec.building": "Building Size",
    "rec.year": "Year Built",
    "rec.assessed": "Assessed Value",
    "rec.landValue": "Land Value",
    "rec.buildingValue": "Building Value",
    "rec.beds": "Bedrooms",
    "rec.baths": "Bathrooms",
    "rec.units": "Units",
    "rec.buildings": "Buildings",
    "rec.subdivision": "Subdivision",
    "rec.legal": "Legal Description",
    "rec.na": "Not available from connected sources",
    "zon.futureLandUse": "Future Land Use",
    "zon.countyLandUse": "County Land Use",
    "zon.responsible": "Zoning Authority",
    "src.retrieved": "Retrieved",
    "src.view": "View source",
    "viol.case": "Case #",
    "viol.type": "Violation",
    "viol.status": "Status",
    "viol.opened": "Opened",
    "viol.lien": "Lien",
    "viol.open": "Open",
    "viol.closed": "Closed",
    "insp.permit": "Permit #",
    "insp.lastDate": "Last Inspection",
    "insp.lookup": "Official inspection lookup",
    "insp.note": "Detailed pass/fail inspection results are not published as an open data feed. Official last-inspection dates from county permit records are shown below; use the official lookup for full history.",
    "report.download": "Download PDF",
    "report.generated": "Generated by Wise Property Search",
    "report.save": "Save Report",
    "report.saved": "Saved",
    "saved.title": "Saved reports",
    "permit.portals": "Official municipal permit search",
    "report.share": "Share",
    "report.linkCopied": "Share link copied",
    "cmp.button": "Compare",
    "cmp.title": "Compare Properties",
    "cmp.hint": "Select two saved reports to compare",
    "tab.overview": "Overview",
    "tab.permits": "Permits",
    "tab.violations": "Violations",
    "tab.inspections": "Inspections",
    "tab.zoning": "Zoning",
    "tab.ask": "Ask Wise Property Search",
    "ask.subtitle": "Ask about permits, violations, remodeling or construction.",
    "ask.placeholder": "Ask about this property…",
    "ask.send": "Ask",
    "ask.project": "Planning a project?",
    "ask.violationHelp": "Need help resolving this violation?",
    "ask.cta": "Need professional permit assistance?",
    "ask.ctaBtn": "Request Permit Assistance",
    "ask.verified": "Verified official data",
    "ask.guidance": "Wise Property Search guidance",
    "ask.zoningExplain": "What does this zoning allow?",
    "ask.manualPermit": "Have a Permit # or Process #?",
    "ask.manualCase": "Have a Case # or identifier?",
    "ask.disclaimer": "Wise Property Search provides informational guidance based on available public records. Final requirements must be confirmed with the applicable building, zoning or code enforcement authority.",
    "ask.name": "Name",
    "ask.email": "Email",
    "ask.desc": "Describe your project",
    "ask.submit": "Submit request",
    "ask.submitted": "Request received — we'll follow up by email.",
    "pa.title": "Project Advisor",
    "pa.subtitle": "Planning a renovation or construction project? Tell Wise Property Search what you want to do and get a preliminary roadmap for permits, zoning, plans, professionals and inspections.",
    "pa.startAddr": "Start with the property address",
    "pa.what": "What are you planning to do?",
    "pa.descph": "Describe your project (e.g. remodel the house, add a bedroom and bath, replace windows, new A/C)…",
    "pa.analyze": "Analyze project",
    "pa.reportDesc": "Get a property-specific roadmap for your renovation, construction or legalization project.",
    "pa.reportBtn": "Create My Project Report",
    "pa.reportSoon": "Project Report checkout is coming soon.",
    "ph.title": "Permit Help",
    "ph.subtitle": "Have a permit, violation or inspection problem? Enter your property or case information and Wise Property Search will help you understand what is happening and the likely next steps.",
    "ph.startAddr": "Search by property address, folio, permit, process or case #",
    "ph.what": "What do you need help with?",
    "assist.title": "Need professional assistance?",
    "assist.btn": "Request Permit Help",
    "assist.sit": "Describe your situation",
    "viol.case": "Case #",
    "viol.type": "Type",
    "viol.status": "Status",
    "viol.opened": "Opened",
    "viol.lien": "Lien",
    "viol.lienYes": "Lien referral",
    "insp.permit": "Permit #",
    "insp.lastDate": "Last Inspection",
    "insp.note": "Detailed pass/fail inspection results are not published as an open data feed. Official last-inspection dates from county permits are shown below; use the official lookup for full history.",
    "insp.lookup": "Official inspection lookup",
    "export.pdf": "Download PDF",
    // Nav / product cards
    "nav.search": "Property Search",
    "nav.advisor": "Project Advisor",
    "nav.help": "Permit Help",
    "card.search.desc": "Check property records, permits, violations, zoning and inspections.",
    "card.advisor.desc": "Understand what you may need before remodeling or building.",
    "card.help.desc": "Get guidance for permits, violations and inspection issues.",
    // Assistance form
    "assist.nameEmail": "Name and email are required.",
    "assist.submitErr": "Could not submit request.",
    "assist.contractorBtn": "Request a contractor",
    "assist.contractorTitle": "Connect with a licensed contractor",
    "assist.contractorDesc": "Tell us about your project and we'll help connect you with a licensed contractor.",
    "assist.phone": "Phone (optional)",
    // Project chips
    "cp.remodel": "Remodel",
    "cp.addition": "Addition",
    "cp.newconstruction": "New Construction",
    "cp.legalize": "Legalize Existing Work",
    "cp.changeuse": "Change of Use",
    "cp.duplex": "Duplex / Multifamily",
    "cp.windows": "Windows & Doors",
    "cp.roof": "Roof",
    "cp.pool": "Pool",
    "cp.electrical": "Electrical",
    "cp.plumbing": "Plumbing",
    "cp.mechanical": "Mechanical / A/C",
    "cp.other": "Other",
    // Issue chips
    "ci.openpermit": "Open Permit",
    "ci.expiredpermit": "Expired Permit",
    "ci.violation": "Violation",
    "ci.nowork": "Work Without Permit",
    "ci.failedinsp": "Failed Inspection",
    "ci.pendinginsp": "Pending Inspection",
    "ci.planreview": "Plan Review Comments",
    "ci.legalize": "Legalize Existing Work",
    "ci.closure": "Permit Closure",
    "ci.certificate": "Certificate of Completion / Occupancy",
    "ci.other": "Other",
    // Project report product
    "pa.reportTitle": "Wise Property Search Project Report — $29.99",
    "pa.unlocked": "Project Report unlocked",
    "pa.viewReport": "View My Project Report",
    // Full property analysis product
    "fa.title": "Full Property Analysis — $14.99",
    "fa.desc": "Get an in-depth, plain-language analysis of this property's permits, violations, zoning and risks — generated from the official records above.",
    "fa.buy": "Get Full Analysis",
    "fa.unlocked": "Full Analysis unlocked",
    "fa.generating": "Preparing your analysis…",
    "fa.regenerate": "Refresh analysis",
    // Payment result
    "pay.processing": "Confirming your payment…",
    "pay.success": "Payment successful",
    "pay.successDesc": "Your {product} is now unlocked.",
    "pay.continue": "Continue",
    "pay.cancelled": "Payment cancelled",
    "pay.cancelledDesc": "No charge was made. You can try again anytime.",
    "pay.failed": "We couldn't confirm your payment.",
    "pay.back": "Back to Wise Property Search",
    "pay.startErr": "Could not start checkout. Please try again.",
    // Auth
    "auth.createTitle": "Create your free account",
    "auth.signinTitle": "Sign in",
    "auth.forgotTitle": "Reset password",
    "auth.createDesc": "Create a free Wise Property Search account to view the complete property report.",
    "auth.signinDesc": "Welcome back — sign in to view your reports.",
    "auth.forgotDesc": "Enter your email to receive a password reset link.",
    "auth.firstName": "First name",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.phoneOpt": "Phone (optional)",
    "auth.createBtn": "Create free account",
    "auth.signinBtn": "Sign in",
    "auth.forgotBtn": "Send reset link",
    "auth.haveAccount": "Already have an account? Sign in",
    "auth.noAccount": "New here? Create a free account",
    "auth.forgotLink": "Forgot password?",
    "auth.noCard": "Free account required. No credit card.",
    "auth.resetSent": "If an account exists, a reset link has been generated.",
    "auth.signIn": "Sign in",
    "auth.signOut": "Sign out",
    // Preview / free report
    "preview.badge": "Property found",
    "preview.title": "We found this property",
    "preview.cta": "Get My Free Property Report",
    "preview.ctaAlt": "View Free Report",
    "preview.support": "Create a free Wise Property Search account to view the complete property report.",
    "preview.locked": "Full report — permits, violations, inspections & zoning",
    "preview.indicators": "What we detected",
    "free.badge": "Free Property Report",
    "save.title": "Save & receive this report",
    "save.desc": "Create a free account to save this report to your dashboard and download it as a PDF.",
    "save.cta": "Create free account",
    "save.saved": "Saved to your Wise Property Search account.",
    // Dashboard
    "nav.dashboard": "My Wise Property Search",
    "dash.title": "My Wise Property Search",
    "dash.properties": "My Properties",
    "dash.reports": "My Reports",
    "dash.projects": "My Projects",
    "dash.questions": "My Questions",
    "dash.empty": "Nothing here yet.",
    "dash.open": "Open report",
    // Professional assistance / services
    "pro.title": "Need professional help?",
    "pro.desc": "Get professional assistance with permits, violations, plans or construction.",
    "pro.btn": "Request Professional Assistance",
    "pro.service": "What do you need?",
    "pro.preferred": "Preferred contact method",
    "pro.attach": "Attach photos (optional)",
    "pro.consent": "I authorize Wise Property Search and its partner professionals to contact me about this request by email, phone or text message.",
    "pro.consentReq": "Please authorize contact to continue.",
    "pro.email": "Email",
    "pro.phone": "Phone",
    "pro.either": "Either",
    "banner.title": "Talk to a professional",
    "banner.desc": "Contractors, permit advisors, renovation, real estate & new construction experts.",
    "svc.contractor": "Multi-Trade Contractor Services",
    "svc.permit": "Request Plans & Permit Assistance",
    "svc.renovation": "Renovation & Repairs Contractor",
    "svc.realestate": "Real estate consultant",
    "svc.newconstruction": "New construction consulting & management",
    // Transects
    "t.T3.label": "Sub-Urban",    "t.T3.scale": "Low-density, primarily single-family and duplex neighborhoods.",
    "t.T4.label": "General Urban",
    "t.T4.scale": "Low-to-mid-rise mixed residential; small-scale commercial permitted.",
    "t.T5.label": "Urban Center",
    "t.T5.scale": "Mid-rise mixed-use with retail, offices and higher-density housing.",
    "t.T6.label": "Urban Core",
    "t.T6.scale": "High-density, high-rise mixed-use — the most intense urban form.",
    "t.CS.label": "Civic Space",
    "t.CS.scale": "Parks, greens, squares and public open space.",
    "t.CI.label": "Civic Institution",
    "t.CI.scale": "Schools, government, religious and community facilities.",
    "t.D1.label": "Work Place District",
    "t.D1.scale": "Light industrial and commercial work districts.",
    "t.D2.label": "Industrial District",
    "t.D2.scale": "General industrial uses.",
    "t.D3.label": "Marine District",
    "t.D3.scale": "Waterfront and marine-dependent industrial uses.",
  },
  es: {
    // Header
    "header.subtitle": "Infórmate Antes de Comprar, Construir o Invertir.",
    "brand.tagline": "Infórmate Antes de Comprar, Construir o Invertir.",
    "header.live": "ArcGIS en vivo",
    "header.toggleTheme": "Cambiar tema",
    "header.language": "Idioma",
    // Hero
    "hero.eyebrow": "Datos Públicos Oficiales · Condado Miami-Dade y Ciudad de Miami",
    "hero.title1": "Conoce la Propiedad",
    "hero.title2": "Antes de Tomar la Decisión.",
    "hero.subtitle":
      "Consulta registros oficiales de propiedad, permisos, violaciones, zonificación e inspecciones desde un solo lugar.",
    "search.valueprop": "Ingresa una dirección. Entiende la propiedad. Toma una mejor decisión.",
    "search.categories": "Permisos · Violaciones · Zonificación · Registros · Inspecciones",
    // Search
    "search.placeholder": "Ingresa una dirección de propiedad",
    "search.button": "Consultar Propiedad",
    "search.loading": "Consultando…",
    "search.try": "Prueba",
    // Benefits
    "benefits.heading": "Una búsqueda. Una decisión más clara.",
    "benefits.buy.title": "Comprar",
    "benefits.buy.desc": "Entiende información importante de la propiedad antes de comprar.",
    "benefits.build.title": "Construir",
    "benefits.build.desc": "Revisa la zonificación, jurisdicción y reglas de desarrollo antes de construir.",
    "benefits.invest.title": "Invertir",
    "benefits.invest.desc": "Evalúa el uso de la propiedad y el potencial de desarrollo antes de invertir.",
    // Recent
    "recent.title": "Búsquedas recientes",
    "recent.clear": "Borrar",
    // Empty / error
    "empty.title": "Comienza con una dirección",
    "empty.subtitle": "Prueba una de las direcciones de ejemplo de arriba para ver un informe completo con fuentes citadas.",
    "error.title": "Nada para mostrar",
    "error.notFound": "No se encontró ninguna propiedad coincidente.",
    "error.failed": "La consulta falló. Un servicio gubernamental puede estar temporalmente no disponible.",
    "toast.failed": "La consulta falló. Inténtalo de nuevo.",
    // Report
    "report.siteAddress": "Dirección del Predio",
    "report.folio": "Folio de la Parcela",
    "report.copy": "Copiar",
    "report.copied": "Copiado",
    "report.municipality": "Municipio / Jurisdicción",
    "report.lotSize": "Tamaño del Lote",
    "report.propertyUse": "Uso de la Propiedad (DOR)",
    "report.landUse": "Uso del Suelo",
    "report.owner": "Propietario Registrado",
    // Zoning
    "zoning.header": "Miami 21 · Zonificación Principal",
    "zoning.transect": "Transecto",
    "zoning.intensity": "Intensidad",
    "zoning.flr": "FLR",
    "zoning.about": "Acerca de Miami 21 →",
    // Building
    "building.header": "Edificación y Avalúo",
    "building.yearBuilt": "Año de Construcción",
    "building.units": "Unidades",
    "building.buildings": "Edificios",
    "building.heatedArea": "Área Climatizada",
    "building.landValue": "Valor del Terreno",
    "building.totalValue": "Valor Total",
    // Source badge
    "source.official": "Fuente de datos oficial",
    "source.retrieved": "Obtenido {x}",
    "source.opensRest": "Abre el servicio ArcGIS REST JSON en una pestaña nueva",
    // Service status
    "status.header": "Estado de Servicios Gubernamentales",
    "status.live": "En vivo · Sin datos simulados",
    "status.probing": "Verificando servicios…",
    "status.phase2": " · Fase 2 (arquitectura lista)",
    "status.offline": "sin conexión",
    // Map
    "map.header": "Geometría de la Parcela",
    "map.openMaps": "Abrir en Maps",
    "map.none": "No hay geometría de parcela disponible.",
    "map.centroid": "Centroide {lat}, {lng} · WGS84 · Parcelas Miami-Dade (Capa 26)",
    // Provenance
    "prov.open": "Ver JSON de procedencia",
    "prov.title": "Carga de Procedencia Normalizada",
    // Footer
    "footer.disclaimer":
      "Wise Property Search consulta en tiempo real los servidores públicos ArcGIS REST operados por el Condado Miami-Dade y la Ciudad de Miami. Los datos se proporcionan tal cual desde fuentes oficiales con fines informativos y no sustituyen una búsqueda oficial de registros ni un levantamiento topográfico.",
    // Dashboard
    "dash.title": "Property Check",
    "sum.permits": "Permisos",
    "sum.openPermits": "Permisos Abiertos",
    "sum.violations": "Violaciones",
    "sum.openViolations": "Violaciones Abiertas",
    "sum.failedInspections": "Inspecciones Fallidas",
    "sum.zoning": "Zonificación",
    "sum.na": "N/D",
    "sec.overview": "Resumen de la Propiedad",
    "sec.permits": "Permisos",
    "sec.violations": "Violaciones",
    "sec.zoning": "Zonificación",
    "sec.inspections": "Inspecciones",
    "sec.records": "Registros de Propiedad",
    "sec.sources": "Fuentes Oficiales",
    "permit.number": "Permiso #",
    "permit.type": "Tipo",
    "permit.desc": "Descripción",
    "permit.issued": "Emitido",
    "permit.status": "Estado",
    "permit.authority": "Autoridad",
    "permit.open": "Abierto",
    "permit.closed": "Cerrado",
    "notice.none": "No se encontraron registros en las fuentes oficiales actualmente conectadas.",
    "notice.unavailable": "Fuente temporalmente no disponible.",
    "notice.notConnected": "No hay registros disponibles en las fuentes oficiales actualmente conectadas.",
    "rec.owner": "Propietario",
    "rec.use": "Uso de la Propiedad",
    "rec.landUse": "Uso del Suelo",
    "rec.lot": "Tamaño del Lote",
    "rec.building": "Área de Edificación",
    "rec.year": "Año de Construcción",
    "rec.assessed": "Valor Tasado",
    "rec.landValue": "Valor del Terreno",
    "rec.buildingValue": "Valor de Edificación",
    "rec.beds": "Habitaciones",
    "rec.baths": "Baños",
    "rec.units": "Unidades",
    "rec.buildings": "Edificios",
    "rec.subdivision": "Subdivisión",
    "rec.legal": "Descripción Legal",
    "rec.na": "No disponible en fuentes conectadas",
    "zon.futureLandUse": "Uso Futuro del Suelo",
    "zon.countyLandUse": "Uso del Suelo (Condado)",
    "zon.responsible": "Autoridad de Zonificación",
    "src.retrieved": "Obtenido",
    "src.view": "Ver fuente",
    "viol.case": "Caso #",
    "viol.type": "Violación",
    "viol.status": "Estado",
    "viol.opened": "Abierto",
    "viol.lien": "Gravamen",
    "viol.open": "Abierto",
    "viol.closed": "Cerrado",
    "insp.permit": "Permiso #",
    "insp.lastDate": "Última Inspección",
    "insp.lookup": "Búsqueda oficial de inspecciones",
    "insp.note": "Los resultados detallados de aprobado/reprobado no se publican como datos abiertos. Se muestran las fechas oficiales de última inspección de los permisos del condado; use la búsqueda oficial para el historial completo.",
    "report.download": "Descargar PDF",
    "report.generated": "Generado por Wise Property Search",
    "report.save": "Guardar Reporte",
    "report.saved": "Guardado",
    "saved.title": "Reportes guardados",
    "permit.portals": "Búsqueda oficial de permisos municipales",
    "report.share": "Compartir",
    "report.linkCopied": "Enlace copiado",
    "cmp.button": "Comparar",
    "cmp.title": "Comparar Propiedades",
    "cmp.hint": "Selecciona dos reportes guardados para comparar",
    "tab.overview": "Resumen",
    "tab.permits": "Permisos",
    "tab.violations": "Violaciones",
    "tab.inspections": "Inspecciones",
    "tab.zoning": "Zonificación",
    "tab.ask": "Ask Wise Property Search",
    "ask.subtitle": "Pregunta sobre permisos, violaciones, remodelación o construcción.",
    "ask.placeholder": "Pregunta sobre esta propiedad…",
    "ask.send": "Preguntar",
    "ask.project": "¿Planeas un proyecto?",
    "ask.violationHelp": "¿Necesitas ayuda para resolver esta violación?",
    "ask.cta": "¿Necesitas ayuda profesional con permisos?",
    "ask.ctaBtn": "Solicitar Ayuda con Permisos",
    "ask.verified": "Datos oficiales verificados",
    "ask.guidance": "Guía de Wise Property Search",
    "ask.zoningExplain": "¿Qué permite esta zonificación?",
    "ask.manualPermit": "¿Tienes un # de Permiso o Proceso?",
    "ask.manualCase": "¿Tienes un # de Caso o identificador?",
    "ask.disclaimer": "Wise Property Search ofrece orientación informativa basada en registros públicos disponibles. Los requisitos finales deben confirmarse con la autoridad de construcción, zonificación o cumplimiento de código correspondiente.",
    "ask.name": "Nombre",
    "ask.email": "Correo",
    "ask.desc": "Describe tu proyecto",
    "ask.submit": "Enviar solicitud",
    "ask.submitted": "Solicitud recibida — te contactaremos por correo.",
    "pa.title": "Asesor de Proyectos",
    "pa.subtitle": "¿Planeas una renovación o construcción? Dile a Wise Property Search qué quieres hacer y obtén una hoja de ruta preliminar de permisos, zonificación, planos, profesionales e inspecciones.",
    "pa.startAddr": "Comienza con la dirección de la propiedad",
    "pa.what": "¿Qué estás planeando hacer?",
    "pa.descph": "Describe tu proyecto (ej. remodelar la casa, agregar recámara y baño, cambiar ventanas, nuevo A/C)…",
    "pa.analyze": "Analizar proyecto",
    "pa.reportDesc": "Obtén una hoja de ruta específica para tu proyecto de renovación, construcción o legalización.",
    "pa.reportBtn": "Crear Mi Reporte de Proyecto",
    "pa.reportSoon": "El pago del Reporte de Proyecto estará disponible pronto.",
    "ph.title": "Ayuda con Permisos",
    "ph.subtitle": "¿Tienes un problema de permiso, violación o inspección? Ingresa tu propiedad o caso y Wise Property Search te ayudará a entender qué ocurre y los próximos pasos probables.",
    "ph.startAddr": "Busca por dirección, folio, permiso, proceso o # de caso",
    "ph.what": "¿En qué necesitas ayuda?",
    "assist.title": "¿Necesitas ayuda profesional?",
    "assist.btn": "Solicitar Ayuda con Permisos",
    "assist.sit": "Describe tu situación",
    "viol.case": "Caso #",
    "viol.type": "Tipo",
    "viol.status": "Estado",
    "viol.opened": "Abierto",
    "viol.lien": "Gravamen",
    "viol.lienYes": "Remisión de gravamen",
    "insp.permit": "Permiso #",
    "insp.lastDate": "Última Inspección",
    "insp.note": "Los resultados detallados de inspección (aprobado/reprobado) no se publican como feed abierto. Se muestran las fechas oficiales de última inspección de permisos del condado; usa la consulta oficial para el historial completo.",
    "insp.lookup": "Consulta oficial de inspecciones",
    "export.pdf": "Descargar PDF",
    // Nav / product cards
    "nav.search": "Buscar Propiedad",
    "nav.advisor": "Asesor de Proyectos",
    "nav.help": "Ayuda con Permisos",
    "card.search.desc": "Consulta registros, permisos, violaciones, zonificación e inspecciones.",
    "card.advisor.desc": "Entiende qué puedes necesitar antes de remodelar o construir.",
    "card.help.desc": "Obtén orientación sobre permisos, violaciones e inspecciones.",
    // Assistance form
    "assist.nameEmail": "Nombre y correo son obligatorios.",
    "assist.submitErr": "No se pudo enviar la solicitud.",
    "assist.contractorBtn": "Solicitar un contratista",
    "assist.contractorTitle": "Conéctate con un contratista con licencia",
    "assist.contractorDesc": "Cuéntanos sobre tu proyecto y te ayudaremos a conectar con un contratista con licencia.",
    "assist.phone": "Teléfono (opcional)",
    // Project chips
    "cp.remodel": "Remodelación",
    "cp.addition": "Ampliación",
    "cp.newconstruction": "Construcción Nueva",
    "cp.legalize": "Legalizar Trabajo Existente",
    "cp.changeuse": "Cambio de Uso",
    "cp.duplex": "Dúplex / Multifamiliar",
    "cp.windows": "Ventanas y Puertas",
    "cp.roof": "Techo",
    "cp.pool": "Piscina",
    "cp.electrical": "Eléctrico",
    "cp.plumbing": "Plomería",
    "cp.mechanical": "Mecánico / A/C",
    "cp.other": "Otro",
    // Issue chips
    "ci.openpermit": "Permiso Abierto",
    "ci.expiredpermit": "Permiso Vencido",
    "ci.violation": "Violación",
    "ci.nowork": "Trabajo Sin Permiso",
    "ci.failedinsp": "Inspección Reprobada",
    "ci.pendinginsp": "Inspección Pendiente",
    "ci.planreview": "Comentarios de Revisión de Planos",
    "ci.legalize": "Legalizar Trabajo Existente",
    "ci.closure": "Cierre de Permiso",
    "ci.certificate": "Certificado de Terminación / Ocupación",
    "ci.other": "Otro",
    // Project report product
    "pa.reportTitle": "Reporte de Proyecto Wise Property Search — $29.99",
    "pa.unlocked": "Reporte de Proyecto desbloqueado",
    "pa.viewReport": "Ver Mi Reporte de Proyecto",
    // Full property analysis product
    "fa.title": "Análisis Completo de la Propiedad — $14.99",
    "fa.desc": "Obtén un análisis detallado y en lenguaje claro de los permisos, violaciones, zonificación y riesgos de esta propiedad — generado a partir de los registros oficiales de arriba.",
    "fa.buy": "Obtener Análisis Completo",
    "fa.unlocked": "Análisis Completo desbloqueado",
    "fa.generating": "Preparando tu análisis…",
    "fa.regenerate": "Actualizar análisis",
    // Payment result
    "pay.processing": "Confirmando tu pago…",
    "pay.success": "Pago exitoso",
    "pay.successDesc": "Tu {product} ya está desbloqueado.",
    "pay.continue": "Continuar",
    "pay.cancelled": "Pago cancelado",
    "pay.cancelledDesc": "No se realizó ningún cargo. Puedes intentarlo cuando quieras.",
    "pay.failed": "No pudimos confirmar tu pago.",
    "pay.back": "Volver a Wise Property Search",
    "pay.startErr": "No se pudo iniciar el pago. Inténtalo de nuevo.",
    // Auth
    "auth.createTitle": "Crea tu cuenta gratis",
    "auth.signinTitle": "Iniciar sesión",
    "auth.forgotTitle": "Restablecer contraseña",
    "auth.createDesc": "Crea una cuenta gratis de Wise Property Search para ver el reporte completo de la propiedad.",
    "auth.signinDesc": "Bienvenido de nuevo — inicia sesión para ver tus reportes.",
    "auth.forgotDesc": "Ingresa tu correo para recibir un enlace de restablecimiento.",
    "auth.firstName": "Nombre",
    "auth.email": "Correo",
    "auth.password": "Contraseña",
    "auth.phoneOpt": "Teléfono (opcional)",
    "auth.createBtn": "Crear cuenta gratis",
    "auth.signinBtn": "Iniciar sesión",
    "auth.forgotBtn": "Enviar enlace",
    "auth.haveAccount": "¿Ya tienes cuenta? Inicia sesión",
    "auth.noAccount": "¿Nuevo aquí? Crea una cuenta gratis",
    "auth.forgotLink": "¿Olvidaste tu contraseña?",
    "auth.noCard": "Cuenta gratis requerida. Sin tarjeta de crédito.",
    "auth.resetSent": "Si existe una cuenta, se generó un enlace de restablecimiento.",
    "auth.signIn": "Iniciar sesión",
    "auth.signOut": "Cerrar sesión",
    // Preview / free report
    "preview.badge": "Propiedad encontrada",
    "preview.title": "Encontramos esta propiedad",
    "preview.cta": "Obtener Mi Reporte Gratis",
    "preview.ctaAlt": "Ver Reporte Gratis",
    "preview.support": "Crea una cuenta gratis de Wise Property Search para ver el reporte completo de la propiedad.",
    "preview.locked": "Reporte completo — permisos, violaciones, inspecciones y zonificación",
    "preview.indicators": "Lo que detectamos",
    "free.badge": "Reporte Gratis",
    "save.title": "Guarda y recibe este reporte",
    "save.desc": "Crea una cuenta gratis para guardar este reporte en tu panel y descargarlo en PDF.",
    "save.cta": "Crear cuenta gratis",
    "save.saved": "Guardado en tu cuenta de Wise Property Search.",
    // Dashboard
    "nav.dashboard": "Mi Wise Property Search",
    "dash.title": "Mi Wise Property Search",
    "dash.properties": "Mis Propiedades",
    "dash.reports": "Mis Reportes",
    "dash.projects": "Mis Proyectos",
    "dash.questions": "Mis Preguntas",
    "dash.empty": "Aún no hay nada aquí.",
    "dash.open": "Abrir reporte",
    // Professional assistance / services
    "pro.title": "¿Necesitas ayuda profesional?",
    "pro.desc": "Obtén asistencia profesional con permisos, violaciones, planos o construcción.",
    "pro.btn": "Solicitar Asistencia Profesional",
    "pro.service": "¿Qué necesitas?",
    "pro.preferred": "Método de contacto preferido",
    "pro.attach": "Adjuntar fotos (opcional)",
    "pro.consent": "Autorizo a Wise Property Search y a sus profesionales asociados a contactarme sobre esta solicitud por correo, teléfono o mensaje de texto.",
    "pro.consentReq": "Debes autorizar el contacto para continuar.",
    "pro.email": "Correo",
    "pro.phone": "Teléfono",
    "pro.either": "Cualquiera",
    "banner.title": "Habla con un profesional",
    "banner.desc": "Contratistas, asesores de permisos, renovación, inmobiliaria y construcción nueva.",
    "svc.contractor": "Servicios de contratista multioficio",
    "svc.permit": "Solicitar planos y asistencia de permisos",
    "svc.renovation": "Contratista de renovación y reparaciones",
    "svc.realestate": "Consultor inmobiliario",
    "svc.newconstruction": "Consultoría y gestión de construcción nueva",
    // Transects
    "t.T3.label": "Sub-Urbano",
    "t.T3.scale": "Baja densidad, principalmente vecindarios unifamiliares y dúplex.",
    "t.T4.label": "Urbano General",
    "t.T4.scale": "Residencial mixto de baja a media altura; se permite comercio a pequeña escala.",
    "t.T5.label": "Centro Urbano",
    "t.T5.scale": "Uso mixto de mediana altura con comercio, oficinas y vivienda de mayor densidad.",
    "t.T6.label": "Núcleo Urbano",
    "t.T6.scale": "Uso mixto de alta densidad y gran altura — la forma urbana más intensa.",
    "t.CS.label": "Espacio Cívico",
    "t.CS.scale": "Parques, áreas verdes, plazas y espacio público abierto.",
    "t.CI.label": "Institución Cívica",
    "t.CI.scale": "Escuelas, gobierno, instalaciones religiosas y comunitarias.",
    "t.D1.label": "Distrito de Trabajo",
    "t.D1.scale": "Distritos industriales ligeros y comerciales.",
    "t.D2.label": "Distrito Industrial",
    "t.D2.scale": "Usos industriales generales.",
    "t.D3.label": "Distrito Marino",
    "t.D3.scale": "Usos industriales frente al mar y dependientes del mar.",
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem(STORE_KEY) || "en");

  useEffect(() => {
    localStorage.setItem(STORE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key, params) => {
    let s = (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key;
    if (params) {
      Object.keys(params).forEach((p) => {
        s = s.replace(`{${p}}`, params[p]);
      });
    }
    return s;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
